import { IconAssignmentLine, List, Text, ToggleDetails } from "@instructure/ui";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import {
  ERROR,
  useNotification,
} from "../../../../../components/CustomHooks/NotificationProvider";
import { AssessmentPlanService } from "../../../../../services/AssessmentService";
import { AssignmentService } from "../../../../../services/AssignmentService";
import { ResultService } from "../../../../../services/ResultService";
import { sort } from "../../../../../utils/utils";
import AssignmentCard from "../AssignmentCard";

const AssignmentsToggle = ({
  section,
  selectedAssignment,
  handleSectionSelect,
  handleAssignmentSelect,
  setChartLoad,
  setSections,
}) => {
  const showNotification = useNotification();
  const [sectionResults, setSectionResults] = useState(null);

  // If section.canvas_id is -1, it's an external context. Otherwise, it's a Canvas course.
  const section_id = section.canvas_id !== -1 ? section.canvas_id : section.id;

  const fetchResults = useCallback(
    async (associatedPlans) => {
      try {
        const results = await ResultService.getResultsBySectionCanvasIds([
          section_id,
        ]);
        // If the section id is not in the results (no results for section, yet), create an empty object for it
        if (!results[section_id]) {
          results[section_id] = {};
        }

        let results_list = [];
        if (section.canvas_id === -1) {
          // Fetch assignments for external contexts
          const planPromiseList = associatedPlans.map((plan) =>
            AssignmentService.getAssignmentsByPlanId(plan.id)
          );
          const planAssignments = await Promise.all(planPromiseList);
          const assignments = planAssignments.flat();

          assignments.forEach((assignment) => {
            // Verify if the assignment is associated with the current section
            if (
              verifyCourseId(assignment, section_id) ||
              verifyCourseIdExternal(assignment, section)
            ) {
              // Add results if they exist, otherwise add the assignment only
              if (assignment.id in results[section_id]) {
                results_list.push({
                  ...results[section_id][assignment.id],
                  assignment,
                });
              } else {
                results_list.push({ assignment });
              }
            }
          });
        } else {
          // Fetch assignments for Canvas course sections
          const assignments = await AssignmentService.getAssignmentsBySectionId(
            section.canvas_id
          );
          assignments.forEach((assignment) => {
            // Add results if they exist, otherwise add the assignment only
            if (assignment.id in results[section_id]) {
              results_list.push({
                ...results[section_id][assignment.id],
                assignment,
              });
            } else {
              results_list.push({ assignment });
            }
          });
        }
        results_list = sort(results_list, (item) => item.assignment.name);
        setSectionResults(results_list);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: `${t("assignment.assignmentsFor")} ${section.name}`,
          }),
          ERROR
        );
      }
    },
    [section, section_id, showNotification]
  );

  const verifyCourseId = (assignment, sectionId) => {
    // handles canvas course section case
    return (
      sectionId !== -1 &&
      parseInt(assignment?.course_id) === parseInt(sectionId)
    );
  };
  const verifyCourseIdExternal = (assignment, section) => {
    //handles external course section case
    return (
      parseInt(assignment.course_id) === -1 &&
      assignment?.ddl_course_id === section.offering.id &&
      assignment?.term_id === section.term.id
    );
  };

  useEffect(() => {
    // get assignments and results on focus
    setChartLoad(true);
    if (section.current && !Object.hasOwn(section, "plans")) {
      AssessmentPlanService.getPlansForSection(section_id, section.term.id)
        .then((plans) => {
          setSections((prevSections) =>
            prevSections.map((sec) => {
              if (sec.id === section.id) {
                return { ...sec, plans: plans };
              }
              return sec;
            })
          );
          fetchResults(plans);
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: `assessment plans for ${section.name}`,
            }),
            ERROR
          );
        });
    }
  }, [
    fetchResults,
    section,
    setChartLoad,
    setSections,
    section_id,
    showNotification,
  ]);

  return (
    <ToggleDetails
      aria-label={t("assignment.assignmentsToggleAria", {
        code: section.offering.code,
        term: section.term?.name,
      })}
      iconPosition="end"
      fluidWidth
      summary={
        <>
          <IconAssignmentLine color="brand" />{" "}
          <Text size="small" weight="bold">
            {t("assignment.assignments")}
          </Text>
        </>
      }
      onToggle={() => handleSectionSelect(section)}
    >
      {sectionResults ? (
        sectionResults.length > 0 ? (
          <List isUnstyled>
            {sectionResults.map((assignment, key) => (
              <List.Item padding="x-small 0" key={key}>
                <AssignmentCard
                  assignment={assignment}
                  selectedAssignment={selectedAssignment}
                  section={section}
                  handleAssignmentSelect={handleAssignmentSelect}
                />
              </List.Item>
            ))}
          </List>
        ) : (
          <Text>{t("dashboard.noAssignmentsInSection")}</Text>
        )
      ) : (
        <Skeleton height={25} count={3} style={{ margin: "0.5rem 0" }} />
      )}
    </ToggleDetails>
  );
};

export default AssignmentsToggle;
